import { Button } from 'antd'
import { wrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useTicketStepContext } from 'features/libra/ticket/TicketStateContext'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import sortBy from 'lodash/sortBy'
import sortedUniq from 'lodash/sortedUniq'
import { FC, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { z } from 'zod'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`

const StyledText = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
`

export const HelloWorkflowStepWorldConfigSchema = z.object({
  name: z.string(),
})

type HelloWorkflowStepWorldProps = z.infer<
  typeof HelloWorkflowStepWorldConfigSchema
>

export const Component: FC<
  LibraWorflowStepComponentProps<HelloWorkflowStepWorldProps>
> = ({ config }) => {
  const { fieldValues, setFieldValue } = useTicketAccess()
  const confirmedNames = useMemo(
    () => (fieldValues.confirmedNames?.value as ReadonlyArray<string>) ?? [],
    [fieldValues.confirmedNames]
  )
  const { isStepComplete, isActiveStep } = useTicketStepContext()

  const isConfirmed = useMemo(
    () => confirmedNames.includes(config.name),
    [confirmedNames, config.name]
  )
  useReportValidation(
    isConfirmed
      ? undefined
      : {
          status: 'Error',
          message: 'Step incomplete - click "Complete this step" to proceed',
        }
  )

  const handleButtonClick = useCallback(() => {
    void setFieldValue(
      'confirmedNames',
      wrapFieldValue(sortedUniq(sortBy([...confirmedNames, config.name])))
    )
  }, [config.name, confirmedNames, setFieldValue])

  return (
    <StyledContainer>
      <StyledText
        style={{
          color: isStepComplete ? 'green' : isActiveStep ? 'blue' : 'red',
        }}>
        Hello {config.name} World!
      </StyledText>
      {isActiveStep && (
        <Button onClick={() => handleButtonClick()}>Complete this step</Button>
      )}
    </StyledContainer>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(
    () => HelloWorkflowStepWorldConfigSchema.safeParse(config).success,
    [config]
  )
}
